import React, { useEffect, useMemo, useState } from "react";
import TooltipIcons from "../../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../../core/common/tooltip-content/collapes";
import {
  DollarSign,
  Download,
  Edit,
  Eye,
  PlusCircle,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import Table from "../../../core/pagination/datatable";
import { Link } from "react-router-dom";
import PosSalesModel from "./pos-salesModal";
import CommonFooter from "../../../core/common/footer/commonFooter";
import { toast } from "react-toastify";
import {
  deletePosOrder,
  downloadPosOrderPdf,
  getAllPosOrders,
  getPosOrderDetail,
} from "../../../api/pos-sales-order";

const getLocalizedText = (value) => {
  if (value === null || value === undefined) return "";
  const currentLang = localStorage.getItem("lang") || "en";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    if (typeof value?.[currentLang] === "string" && value[currentLang].trim()) {
      return value[currentLang];
    }
    if (typeof value?.en === "string" && value.en.trim()) return value.en;
    if (typeof value?.ar === "string" && value.ar.trim()) return value.ar;
    if (typeof value?.name === "string" && value.name.trim()) return value.name;
    if (typeof value?.label === "string" && value.label.trim()) return value.label;
  }
  return "";
};

const PosOrder = () => {
  const t = (key) => (window?.translate ? window.translate(key) : key);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [salesDetails, setSalesDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const storeId = localStorage.getItem("storeId") || "";
      const res = await getAllPosOrders({
        page: 1,
        limit: 100,
        search,
        vendorStoreId: storeId,
      });
      setOrders(res?.data || []);
    } catch (error) {
      toast.error(error?.message || t("Failed to fetch POS orders"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleRefresh = () => {
    fetchOrders();
  };

  const openDetails = async (row) => {
    try {
      setSelectedOrder(row);
      const res = await getPosOrderDetail(row._id);
      setSalesDetails(res?.data || null);
    } catch (error) {
      toast.error(error?.message || t("Failed to fetch sale detail"));
    }
  };

  const openPayments = async (row) => {
    try {
      setSelectedOrder(row);
      const detailRes = await getPosOrderDetail(row._id);
      setSalesDetails(detailRes?.data || null);
    } catch (error) {
      toast.error(error?.message || t("Failed to fetch payments"));
    }
  };

  const handleDelete = async () => {
    if (!selectedOrder?._id) return;
    try {
      await deletePosOrder(selectedOrder._id);
      toast.success(t("POS sale deleted successfully"));
      setSelectedOrder(null);
      setSalesDetails(null);
      fetchOrders();
    } catch (error) {
      toast.error(error?.message || t("Failed to delete sale"));
    }
  };

  const handleDownloadPdf = async (row) => {
    try {
      const blob = await downloadPosOrderPdf(row._id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pos-order-${row.orderId || "invoice"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.message || t("Failed to download pdf"));
    }
  };

  const filteredOrders = useMemo(() => {
    if (!search?.trim()) return orders;
    const q = search.toLowerCase();
    return orders.filter((item) => {
      const userName = getLocalizedText(item?.user?.name).toLowerCase();
      const orderId = getLocalizedText(item?.orderId).toLowerCase();
      const status = getLocalizedText(item?.status).toLowerCase();
      const paymentStatus = getLocalizedText(item?.paymentStatus).toLowerCase();

      return (
        userName.includes(q) ||
        orderId.includes(q) ||
        status.includes(q) ||
        paymentStatus.includes(q)
      );
    });
  }, [orders, search]);

  const columns = [
    {
      title: t("User"),
      dataIndex: ["user", "name"],
      render: (_, row) => <Link to="#">{getLocalizedText(row?.user?.name) || t("N/A")}</Link>,
      sorter: (a, b) =>
        getLocalizedText(a?.user?.name).localeCompare(getLocalizedText(b?.user?.name)),
    },
    {
      title: t("Reference"),
      dataIndex: "orderId",
      render: (value) => getLocalizedText(value) || t("N/A"),
      sorter: (a, b) => getLocalizedText(a?.orderId).localeCompare(getLocalizedText(b?.orderId)),
    },
    {
      title: t("Date"),
      dataIndex: "createdAt",
      render: (value) => (value ? new Date(value).toLocaleDateString() : "--"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: t("Status"),
      dataIndex: "status",
      render: (value) => {
        const statusText = getLocalizedText(value);
        return (
          <span
            className={`badge ${
              statusText === "Pending"
                ? "badge-cyan"
                : statusText === t("Completed") || statusText === "Completed"
                ? "badge-success"
                : "badge-danger"
            }`}
          >
            {statusText ? t(statusText) : t("N/A")}
          </span>
        );
      },
      sorter: (a, b) => getLocalizedText(a?.status).localeCompare(getLocalizedText(b?.status)),
    },
    {
      title: t("Grand Total"),
      dataIndex: "grandTotal",
      render: (value) => `$${Number(value || 0).toFixed(2)}`,
      sorter: (a, b) => Number(a?.grandTotal || 0) - Number(b?.grandTotal || 0),
    },
    {
      title: t("Paid"),
      dataIndex: "paidAmount",
      render: (value) => `$${Number(value || 0).toFixed(2)}`,
      sorter: (a, b) => Number(a?.paidAmount || 0) - Number(b?.paidAmount || 0),
    },
    {
      title: t("Due"),
      dataIndex: "dueAmount",
      render: (value) => `$${Number(value || 0).toFixed(2)}`,
      sorter: (a, b) => Number(a?.dueAmount || 0) - Number(b?.dueAmount || 0),
    },
    {
      title: t("Payment Status"),
      dataIndex: "paymentStatus",
      render: (value) => {
        const paymentStatusText = getLocalizedText(value);
        return (
          <span
            className={`badge badge-xs shadow-none ${
              paymentStatusText === "Unpaid"
                ? "badge-soft-danger"
                : paymentStatusText === "Paid"
                ? "badge-soft-success"
                : "badge-soft-warning"
            }`}
          >
            <i className="ti ti-point-filled me-1"></i>
            {paymentStatusText ? t(paymentStatusText) : t("N/A")}
          </span>
        );
      },
      sorter: (a, b) =>
        getLocalizedText(a?.paymentStatus).localeCompare(
          getLocalizedText(b?.paymentStatus)
        ),
    },
    {
      title: t("Biller"),
      dataIndex: ["user", "name"],
      render: (_, row) =>
        getLocalizedText(row?.paymentReceiver) ||
        getLocalizedText(row?.user?.name) ||
        t("Admin"),
      sorter: (a, b) =>
        (
          getLocalizedText(a?.paymentReceiver) || getLocalizedText(a?.user?.name)
        ).localeCompare(
          getLocalizedText(b?.paymentReceiver) || getLocalizedText(b?.user?.name)
        ),
    },
    {
      title: "",
      dataIndex: "action",
      render: (_, row) => (
        <>
          <Link className="action-set" to="#" data-bs-toggle="dropdown" aria-expanded="true">
            <i className="fa fa-ellipsis-v" aria-hidden="true" />
          </Link>

          <ul className="dropdown-menu">
            <li>
              <Link
                to="#"
                className="dropdown-item"
                data-bs-toggle="modal"
                data-bs-target="#sales-details-new"
                onClick={() => openDetails(row)}
              >
                <Eye className="info-img" />
                {t("Sale Detail")}
              </Link>
            </li>

            <li>
              <Link
                to="#"
                className="dropdown-item"
                data-bs-toggle="modal"
                data-bs-target="#edit-sales-new"
                onClick={() => openDetails(row)}
              >
                <Edit className="info-img" />
                {t("Edit Sale")}
              </Link>
            </li>

            <li>
              <Link
                to="#"
                className="dropdown-item"
                data-bs-toggle="modal"
                data-bs-target="#showpayment"
                onClick={() => openPayments(row)}
              >
                <DollarSign className="info-img" />
                {t("Show Payments")}
              </Link>
            </li>

            <li>
              <Link
                to="#"
                className="dropdown-item"
                data-bs-toggle="modal"
                data-bs-target="#createpayment"
                onClick={() => openDetails(row)}
              >
                <PlusCircle className="info-img" />
                {t("Create Payment")}
              </Link>
            </li>

            <li>
              <Link
                to="#"
                className="dropdown-item"
                onClick={() => handleDownloadPdf(row)}
              >
                <Download className="info-img" />
                {t("Download pdf")}
              </Link>
            </li>

            <li>
              <Link
                to="#"
                className="dropdown-item mb-0"
                data-bs-toggle="modal"
                data-bs-target="#delete-pos-sale-modal"
                onClick={() => setSelectedOrder(row)}
              >
                <Trash2 className="info-img" />
                {t("Delete Sale")}
              </Link>
            </li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("POS Orders")}</h4>
                <h6>{t("Manage Your pos orders")}</h6>
              </div>
            </div>

            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link to="#" onClick={handleRefresh}>
                  <RefreshIcon />
                </Link>
              </li>
              <CollapesIcon />
            </ul>

            <div className="page-btn">
              <Link to="/pos" className="btn btn-primary">
                <i className="ti ti-circle-plus me-2 ms-2"></i> {t("Add Sales")}
              </Link>
            </div>
          </div>

          <div className="card table-list-card manage-stock">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("Search by user, reference, status")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ minWidth: "300px" }}
                />
              </div>

              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <button type="button" className="btn btn-primary" onClick={fetchOrders}>
                  {t("Search")}
                </button>
              </div>
            </div>

            <div className="card-body">
              <div className="custom-datatable-filter table-responsive">
                {loading ? (
                  <div className="text-center py-4">{t("Loading...")}</div>
                ) : (
                  <Table columns={columns} dataSource={filteredOrders} />
                )}
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>

      <PosSalesModel
        selectedOrder={selectedOrder}
        salesDetails={salesDetails}
        onRefresh={fetchOrders}
        onDeleteClick={handleDelete}
      />
    </div>
  );
};

export default PosOrder;
